
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const ComplicationsOutcomesFH = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f04e58]/10 rounded-full mb-6">
              <i className="ri-alert-line text-[#f04e58]"></i>
              <span className="text-sm font-medium text-[#f04e58]">Critical Health Information</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6">
              Complications & Outcomes of <span className="font-semibold text-[#f04e58]">Familial Hypercholesterolemia</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed font-light">
              Why Early Detection Saves Lives
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Most people associate high cholesterol with "future risk."<br />
              But in Familial Hypercholesterolemia (FH), the consequences are not distant — they are <strong className="text-[#f04e58]">accelerated, cumulative, and often severe</strong>.
            </p>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              FH is an inherited condition that causes lifelong elevation of LDL ("bad") cholesterol. Without treatment, this persistent exposure dramatically increases the risk of cardiovascular complications at an earlier age than the general population.
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              Understanding the complications and long-term outcomes of FH is essential to recognizing why awareness and early screening matter.
            </p>
          </div>
        </div>
      </section>

      {/* Complications Grid */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            
            {/* 1. Recurrent Myocardial Infarction */}
            <div className="bg-white rounded-2xl border-2 border-[#f04e58]/20 p-8 hover:border-[#f04e58] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                  <i className="ri-heart-pulse-line text-2xl text-[#f04e58]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#f04e58] mb-1">1️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Recurrent Myocardial Infarction</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                A myocardial infarction (heart attack) occurs when a coronary artery becomes blocked, cutting off blood flow to part of the heart muscle.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">In untreated FH:</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                    <span>First heart attacks often occur before age 50</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                    <span>Plaque burden is typically extensive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                    <span>Risk of a second heart attack is significantly elevated</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Because LDL remains chronically high unless aggressively treated, plaque continues to progress even after the first cardiac event. Without optimal LDL lowering, recurrent myocardial infarction becomes more likely.
              </p>
            </div>

            {/* 2. Stroke Risk in FH */}
            <div className="bg-white rounded-2xl border-2 border-[#8b5cf6]/20 p-8 hover:border-[#8b5cf6] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#8b5cf6]/10 flex-shrink-0">
                  <i className="ri-brain-line text-2xl text-[#8b5cf6]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#8b5cf6] mb-1">2️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Stroke Risk in FH</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                While coronary artery disease is the most recognized complication, FH also increases risk of ischemic stroke.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">Mechanisms include:</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#8b5cf6] mt-0.5 flex-shrink-0"></i>
                    <span>Carotid artery atherosclerosis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#8b5cf6] mt-0.5 flex-shrink-0"></i>
                    <span>Plaque instability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#8b5cf6] mt-0.5 flex-shrink-0"></i>
                    <span>Elevated Lipoprotein(a), commonly seen in FH</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Although stroke risk is generally lower than heart attack risk in FH, patients with severe LDL elevation or additional risk factors face significantly increased cerebrovascular risk.
              </p>
            </div>

            {/* 3. Heart Failure Progression */}
            <div className="bg-white rounded-2xl border-2 border-[#0d9488]/20 p-8 hover:border-[#0d9488] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                  <i className="ri-heart-add-line text-2xl text-[#0d9488]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#0d9488] mb-1">3️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Heart Failure Progression</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Heart failure may develop as a long-term consequence of:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                    <span>Repeated heart attacks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                    <span>Multi-vessel coronary disease</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                    <span>Chronic ischemia</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                    <span>Structural heart changes</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                When heart muscle is damaged, pumping function declines. Over time, patients may experience:
              </p>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Shortness of breath</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Fluid retention</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Reduced exercise tolerance</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed mt-3">
                Heart failure significantly impacts quality of life and long-term survival.
              </p>
            </div>

            {/* 4. Need for CABG */}
            <div className="bg-white rounded-2xl border-2 border-[#e67e22]/20 p-8 hover:border-[#e67e22] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#e67e22]/10 flex-shrink-0">
                  <i className="ri-surgical-mask-line text-2xl text-[#e67e22]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#e67e22] mb-1">4️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Need for Coronary Artery Bypass Surgery (CABG)</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Because FH often leads to multi-vessel coronary artery disease at a young age, patients are more likely to require:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#e67e22] mt-0.5 flex-shrink-0"></i>
                    <span>Coronary artery bypass grafting (CABG)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#e67e22] mt-0.5 flex-shrink-0"></i>
                    <span>Complex revascularization procedures</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Surgery may be necessary when:
              </p>
              <ul className="space-y-1.5 text-sm text-gray-600 mb-4">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#e67e22] mt-0.5 flex-shrink-0"></i>
                  <span>Multiple arteries are blocked</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#e67e22] mt-0.5 flex-shrink-0"></i>
                  <span>Left main coronary artery is involved</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#e67e22] mt-0.5 flex-shrink-0"></i>
                  <span>Stents are insufficient</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed">
                While CABG can restore blood flow, it does not eliminate the underlying genetic lipid disorder.
              </p>
            </div>

            {/* 5. Stent Restenosis */}
            <div className="bg-white rounded-2xl border-2 border-[#3b82f6]/20 p-8 hover:border-[#3b82f6] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#3b82f6]/10 flex-shrink-0">
                  <i className="ri-heart-line text-2xl text-[#3b82f6]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#3b82f6] mb-1">5️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Stent Restenosis</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Percutaneous coronary intervention (PCI) with stent placement is common in FH patients.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">However:</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#3b82f6] mt-0.5 flex-shrink-0"></i>
                    <span>Persistent LDL elevation promotes new plaque growth</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#3b82f6] mt-0.5 flex-shrink-0"></i>
                    <span>In-stent restenosis (re-narrowing) may occur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#3b82f6] mt-0.5 flex-shrink-0"></i>
                    <span>Repeat procedures may be required</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Aggressive lipid lowering significantly reduces this risk.
              </p>
            </div>

            {/* 6. Post-PCI Complications */}
            <div className="bg-white rounded-2xl border-2 border-[#ec4899]/20 p-8 hover:border-[#ec4899] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#ec4899]/10 flex-shrink-0">
                  <i className="ri-stethoscope-line text-2xl text-[#ec4899]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#ec4899] mb-1">6️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Post-PCI Complications</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Following stent placement, complications may include:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#ec4899] mt-0.5 flex-shrink-0"></i>
                    <span>Recurrent angina</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#ec4899] mt-0.5 flex-shrink-0"></i>
                    <span>Thrombosis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#ec4899] mt-0.5 flex-shrink-0"></i>
                    <span>Need for additional interventions</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                In FH patients, the systemic nature of atherosclerosis means untreated arteries may continue to deteriorate even after localized treatment.
              </p>
            </div>

            {/* 7. Early Mortality Risk */}
            <div className="bg-white rounded-2xl border-2 border-[#dc2626]/20 p-8 hover:border-[#dc2626] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#dc2626]/10 flex-shrink-0">
                  <i className="ri-time-line text-2xl text-[#dc2626]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#dc2626] mb-1">7️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Early Mortality Risk</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Historically, untreated FH was associated with markedly shortened lifespan.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">In severe cases:</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#dc2626] mt-0.5 flex-shrink-0"></i>
                    <span>Men with untreated FH had a high risk of fatal coronary events before age 60</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#dc2626] mt-0.5 flex-shrink-0"></i>
                    <span>Individuals with severe Homozygous Familial Hypercholesterolemia (HoFH) faced cardiovascular events in childhood or adolescence</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Modern therapies have dramatically improved outcomes, but delayed diagnosis still contributes to preventable mortality.
              </p>
            </div>

            {/* 8. Gender Differences */}
            <div className="bg-white rounded-2xl border-2 border-[#14b8a6]/20 p-8 hover:border-[#14b8a6] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#14b8a6]/10 flex-shrink-0">
                  <i className="ri-user-heart-line text-2xl text-[#14b8a6]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#14b8a6] mb-1">8️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Gender Differences in FH Outcomes</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Men with FH often experience coronary events earlier than women.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">However:</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-0.5 flex-shrink-0"></i>
                    <span>Women are frequently underdiagnosed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-0.5 flex-shrink-0"></i>
                    <span>Symptoms may be misattributed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-0.5 flex-shrink-0"></i>
                    <span>Treatment may be delayed</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                After menopause, women's cardiovascular risk rises sharply. Awareness and equitable screening are critical.
              </p>
            </div>

            {/* 9. Residual Risk */}
            <div className="bg-white rounded-2xl border-2 border-[#f59e0b]/20 p-8 hover:border-[#f59e0b] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f59e0b]/10 flex-shrink-0">
                  <i className="ri-shield-cross-line text-2xl text-[#f59e0b]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#f59e0b] mb-1">9️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Residual Cardiovascular Risk Despite Therapy</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Even with statins and advanced therapies:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#f59e0b] mt-0.5 flex-shrink-0"></i>
                    <span>Some patients retain elevated risk</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#f59e0b] mt-0.5 flex-shrink-0"></i>
                    <span>Lipoprotein(a) may remain high</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#f59e0b] mt-0.5 flex-shrink-0"></i>
                    <span>Genetic burden influences outcomes</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                This highlights the importance of early, aggressive LDL reduction — ideally before plaque becomes advanced.
              </p>
            </div>

            {/* 10. Lifetime Cardiovascular Risk */}
            <div className="bg-white rounded-2xl border-2 border-[#6366f1]/20 p-8 hover:border-[#6366f1] hover:shadow-lg transition-all md:col-span-2">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#6366f1]/10 flex-shrink-0">
                  <i className="ri-infinity-line text-2xl text-[#6366f1]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#6366f1] mb-1">🔟</div>
                  <h3 className="text-xl font-semibold text-gray-900">Lifetime Cardiovascular Risk</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                FH dramatically increases lifetime risk of:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[#6366f1] mt-0.5 flex-shrink-0"></i>
                      <span>Coronary artery disease</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[#6366f1] mt-0.5 flex-shrink-0"></i>
                      <span>Myocardial infarction</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[#6366f1] mt-0.5 flex-shrink-0"></i>
                      <span>Revascularization procedures</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[#6366f1] mt-0.5 flex-shrink-0"></i>
                      <span>Cardiovascular mortality</span>
                    </li>
                  </ul>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Risk accumulates with <strong className="text-[#6366f1]">"cholesterol years"</strong> — the total duration and magnitude of LDL exposure.
              </p>
              <p className="text-sm font-semibold text-gray-900">
                The earlier LDL is controlled, the lower the cumulative burden.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Why Early Diagnosis Changes Outcomes */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#0d9488] to-[#14b8a6] rounded-2xl p-10 text-white">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-3xl font-semibold mb-2">Why Early Diagnosis Changes Outcomes</h2>
                <p className="text-lg text-white/90">When FH is identified early and treated aggressively:</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-arrow-down-line text-2xl text-white"></i>
                  <h3 className="text-lg font-semibold">LDL Reduction</h3>
                </div>
                <p className="text-sm text-white/90">LDL can be reduced by 50–70% or more</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                  <h3 className="text-lg font-semibold">Heart Attack Risk</h3>
                </div>
                <p className="text-sm text-white/90">Risk can drop by up to 80%</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm md:col-span-2">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-time-line text-2xl text-white"></i>
                  <h3 className="text-lg font-semibold">Life Expectancy</h3>
                </div>
                <p className="text-sm text-white/90">Life expectancy approaches that of the general population</p>
              </div>
            </div>

            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm mb-6">
              <h3 className="text-lg font-semibold mb-4">Therapies include:</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <i className="ri-medicine-bottle-line text-white"></i>
                  <span className="text-sm">High-intensity statins</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-medicine-bottle-line text-white"></i>
                  <span className="text-sm">Ezetimibe</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-medicine-bottle-line text-white"></i>
                  <span className="text-sm">PCSK9 inhibitors</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-medicine-bottle-line text-white"></i>
                  <span className="text-sm">Inclisiran</span>
                </div>
                <div className="flex items-center gap-2 md:col-span-2">
                  <i className="ri-medicine-bottle-line text-white"></i>
                  <span className="text-sm">LDL apheresis (for severe cases)</span>
                </div>
              </div>
            </div>

            <p className="text-base text-white/90">
              Early detection also enables <strong className="text-white">cascade screening</strong> — testing first-degree relatives to prevent future complications across generations.
            </p>
          </div>
        </div>
      </section>

      {/* The Awareness Gap */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">The Awareness Gap</h2>
            <p className="text-lg text-gray-600">A preventable crisis hiding in plain sight</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-2xl border-2 border-[#f04e58]/20 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-4">
                <i className="ri-group-line text-3xl text-[#f04e58]"></i>
              </div>
              <div className="text-4xl font-bold text-[#f04e58] mb-2">1 in 250</div>
              <p className="text-sm text-gray-600">People worldwide affected by FH</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-[#dc2626]/20 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#dc2626]/10 mx-auto mb-4">
                <i className="ri-eye-off-line text-3xl text-[#dc2626]"></i>
              </div>
              <div className="text-4xl font-bold text-[#dc2626] mb-2">90%</div>
              <p className="text-sm text-gray-600">Remain undiagnosed</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-[#e67e22]/20 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#e67e22]/10 mx-auto mb-4">
                <i className="ri-alert-line text-3xl text-[#e67e22]"></i>
              </div>
              <div className="text-4xl font-bold text-[#e67e22] mb-2">Too Late</div>
              <p className="text-sm text-gray-600">Many learn only after a cardiac event</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                <i className="ri-error-warning-line text-2xl text-[#f04e58]"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Yet:</h3>
                <ul className="space-y-2 text-base text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>Up to 90% remain undiagnosed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>Many learn about the condition only after a cardiac event</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>Family members often go untested</span>
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-base text-gray-700 font-semibold mt-6">
              Complications such as recurrent heart attacks, heart failure, and premature death are often preventable with timely diagnosis.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/10 mx-auto mb-6">
                <i className="ri-lightbulb-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl font-semibold mb-4">Final Takeaway</h2>
            </div>

            <div className="space-y-4 mb-8">
              <p className="text-lg text-white/90 leading-relaxed">
                Familial Hypercholesterolemia is not simply "high cholesterol."
              </p>
              
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <p className="text-base text-white/90 mb-3">It is a lifelong exposure to elevated LDL that can lead to:</p>
                <ul className="space-y-2 text-base text-white/90">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Recurrent myocardial infarction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Stroke</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Heart failure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Repeated surgeries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Early mortality</span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-white font-semibold">
                The good news is that outcomes improve dramatically when FH is recognized early.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14b8a6]/20 mx-auto mb-3">
                  <i className="ri-eye-line text-2xl text-[#14b8a6]"></i>
                </div>
                <h3 className="text-base font-semibold mb-1">Awareness</h3>
                <p className="text-sm text-white/80">Know the signs</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14b8a6]/20 mx-auto mb-3">
                  <i className="ri-test-tube-line text-2xl text-[#14b8a6]"></i>
                </div>
                <h3 className="text-base font-semibold mb-1">Testing</h3>
                <p className="text-sm text-white/80">Get screened</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14b8a6]/20 mx-auto mb-3">
                  <i className="ri-heart-pulse-line text-2xl text-[#14b8a6]"></i>
                </div>
                <h3 className="text-base font-semibold mb-1">Early Treatment</h3>
                <p className="text-sm text-white/80">Prevent complications</p>
              </div>
            </div>

            <div className="bg-[#14b8a6]/20 rounded-xl p-6 backdrop-blur-sm mb-6">
              <p className="text-base text-white/90 leading-relaxed">
                <strong className="text-white">Awareness saves lives</strong> — not only for individuals, but for entire families.
              </p>
            </div>

            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <p className="text-base text-white/90 leading-relaxed mb-4">
                If you or someone in your family has <strong className="text-white">LDL above 190 mg/dL</strong> or a history of <strong className="text-white">early heart disease</strong>, consider evaluation for Familial Hypercholesterolemia.
              </p>
              <p className="text-lg text-white font-semibold text-center">
                Because when it comes to FH, prevention begins with knowing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#0d9488] to-[#14b8a6] rounded-2xl p-10 text-center text-white shadow-xl">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-6">
              <i className="ri-heart-pulse-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-semibold mb-4">Early Detection Changes Everything</h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto">
              Understanding the complications of FH is the first step. Early screening and aggressive treatment can prevent these outcomes and save lives.
            </p>
            <a
              href="/consultation"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0d9488] text-base font-semibold rounded-full hover:bg-gray-50 transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-xl"></i>
              Schedule a Consultation
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComplicationsOutcomesFH;
